import React, { useEffect, useState } from 'react';
import { View, ScrollView, StyleSheet, RefreshControl, Alert } from 'react-native';
import { 
  Card, 
  Title, 
  Paragraph, 
  useTheme, 
  Button, 
  ActivityIndicator,
  Chip,
  Divider,
  FAB,
  IconButton,
  Searchbar
} from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth } from '../contexts/AuthContext';
import { catchReportService } from '../services/catchReportService';
import { setCatchReports } from '../store/fishingSlice';
import type { CatchReport, RootStackParamList } from '../types';
import type { RootState, AppDispatch } from '../store';

type NavigationProp = StackNavigationProp<RootStackParamList>;

export default function CatchHistoryScreen() {
  const theme = useTheme();
  const navigation = useNavigation<NavigationProp>();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  
  const catchReports = useSelector((state: RootState) => state.fishing.catchReports || []);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSpecies, setFilterSpecies] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    let unsubscribe: (() => void) | undefined;

    const loadReports = async () => {
      setLoading(true);
      try {
        // Set up real-time listener for user's catch reports
        unsubscribe = catchReportService.listenToUserReports(user.uid, (reports) => {
          dispatch(setCatchReports(reports));
          setLoading(false);
        });
      } catch (error) {
        console.error('Error loading catch reports:', error);
        Alert.alert('Error', 'Failed to load catch history');
        setLoading(false);
      }
    };

    loadReports();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user, dispatch]);

  const onRefresh = async () => {
    if (!user) return;
    
    setRefreshing(true);
    try {
      const reports = await catchReportService.getUserCatchReports(user.uid);
      dispatch(setCatchReports(reports));
    } catch (error) {
      console.error('Error refreshing catch reports:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Filter reports based on search and species filter
  const filteredReports = catchReports.filter(report => {
    const matchesSearch = !searchQuery || 
      report.species.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.notes?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSpecies = !filterSpecies || report.species === filterSpecies;
    
    return matchesSearch && matchesSpecies;
  });

  // Get unique species for filter chips
  const uniqueSpecies = Array.from(new Set(catchReports.map(r => r.species)));

  const formatDate = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      };
      return date.toLocaleDateString('en-US', options);
    } catch {
      return 'Unknown date';
    }
  };

  const renderCatchCard = (report: CatchReport) => (
    <Card key={report.id} style={styles.card} onPress={() => {
      navigation.navigate('CatchDetail', { catchReport: report });
    }}>
      <Card.Content>
        <View style={styles.cardHeader}>
          <View style={styles.speciesInfo}>
            <MaterialCommunityIcons 
              name="fish" 
              size={24} 
              color={theme.colors.primary} 
            />
            <Title style={styles.speciesName}>{report.species}</Title>
          </View>
          <Paragraph style={styles.timestamp}>
            {formatDate(report.timestamp)}
          </Paragraph>
        </View>

        <View style={styles.details}>
          {report.count > 1 && (
            <Chip icon="counter" style={styles.chip}>
              Count: {report.count}
            </Chip>
          )}
          
          {report.averageSize > 0 && (
            <Chip icon="weight" style={styles.chip}>
              {report.averageSize} lbs
            </Chip>
          )}

          {report.depth > 0 && (
            <Chip icon="arrow-down" style={styles.chip}>
              {report.depth} ft
            </Chip>
          )}

          <Chip icon="map-marker" style={styles.chip}>
            {report.location.latitude.toFixed(4)}°, {report.location.longitude.toFixed(4)}°
          </Chip>
        </View>

        {report.notes && (
          <>
            <Divider style={styles.divider} />
            <Paragraph style={styles.notes}>{report.notes}</Paragraph>
          </>
        )}

        {report.conditions && (
          <View style={styles.conditions}>
            {report.conditions.waterTemp > 0 && (
              <Paragraph style={styles.conditionText}>
                Water: {report.conditions.waterTemp}°F
              </Paragraph>
            )}
            {report.conditions.weather && (
              <Paragraph style={styles.conditionText}>
                Weather: {report.conditions.weather}
              </Paragraph>
            )}
          </View>
        )}
      </Card.Content>
    </Card>
  );

  if (loading && catchReports.length === 0) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" />
        <Paragraph style={styles.loadingText}>Loading catch history...</Paragraph>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Searchbar
          placeholder="Search catches..."
          onChangeText={setSearchQuery}
          value={searchQuery}
          style={styles.searchbar}
        />
        
        {uniqueSpecies.length > 0 && (
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            style={styles.filterContainer}
          >
            <Chip
              selected={!filterSpecies}
              onPress={() => setFilterSpecies(null)}
              style={styles.filterChip}
            >
              All
            </Chip>
            {uniqueSpecies.map(species => (
              <Chip
                key={species}
                selected={filterSpecies === species}
                onPress={() => setFilterSpecies(species === filterSpecies ? null : species)}
                style={styles.filterChip}
              >
                {species}
              </Chip>
            ))}
          </ScrollView>
        )}
      </View>

      <ScrollView
        style={styles.scrollView}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {filteredReports.length === 0 ? (
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons 
              name="fish-off" 
              size={64} 
              color={theme.colors.onSurfaceDisabled} 
            />
            <Paragraph style={styles.emptyText}>
              {searchQuery || filterSpecies 
                ? 'No catches match your filters'
                : 'No catches logged yet'}
            </Paragraph>
            {!searchQuery && !filterSpecies && (
              <Button 
                mode="contained" 
                onPress={() => navigation.navigate('CatchReport' as never)}
                style={styles.addButton}
              >
                Log Your First Catch
              </Button>
            )}
          </View>
        ) : (
          filteredReports.map(renderCatchCard)
        )}
      </ScrollView>

      <FAB
        icon="plus"
        style={[styles.fab, { backgroundColor: theme.colors.primary }]}
        onPress={() => navigation.navigate('CatchReport' as never)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  searchbar: {
    marginBottom: 8,
  },
  filterContainer: {
    maxHeight: 50,
    marginBottom: 8,
  },
  filterChip: {
    marginRight: 8,
  },
  scrollView: {
    flex: 1,
    padding: 16,
  },
  card: {
    marginBottom: 16,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  speciesInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  speciesName: {
    marginLeft: 8,
    fontSize: 20,
  },
  timestamp: {
    fontSize: 12,
    opacity: 0.7,
  },
  details: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  chip: {
    marginRight: 8,
    marginBottom: 8,
  },
  divider: {
    marginVertical: 8,
  },
  notes: {
    fontStyle: 'italic',
    marginTop: 4,
  },
  conditions: {
    marginTop: 8,
  },
  conditionText: {
    fontSize: 12,
    opacity: 0.8,
  },
  loadingText: {
    marginTop: 16,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingTop: 80,
  },
  emptyText: {
    marginTop: 16,
    fontSize: 16,
    opacity: 0.7,
  },
  addButton: {
    marginTop: 24,
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
  },
});